import { LOCAL_STORAGE } from 'constants';

import { useEffect, useRef } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { useAuth } from '@pray/shared/hooks/useAuth';
import { navigate } from '@pray/shared/utils/navigation';
import storage from '@pray/shared/utils/storage';

import { initialArtistData, useStudioContext } from 'context/StudioContext';

export default function useAppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const leaderRouteMatch = useMatch('/leaders/:artistId/*');
  const getArtistIdParam = (artistId) => (artistId !== 'profile' ? artistId : '');
  const artistIdUrlParam = getArtistIdParam(leaderRouteMatch?.params?.artistId);
  const { selectedArtist, setSelectedArtist, artists } = useStudioContext();
  const artistId = selectedArtist?.id || artistIdUrlParam;
  const isUserRegistered = !!user?.is_registered;
  const prevUrlParamRef = useRef(artistIdUrlParam);
  // The five AI pages live under /settings/ai for every leader; the old builders follow.
  const isAiSettingsEnabled = true;

  useEffect(() => {
    if (!isUserRegistered) return;

    // @todo - this is a temporary fix for the issue when artistIdUrlParam is not set in url,
    // since the url does not starts with /leaders
    if (!location.pathname.startsWith('/leaders')) {
      return;
    }

    // there's a selected artist id but no artist id url param
    if (selectedArtist?.id && !artistIdUrlParam) {
      const artistProfileRoute = asRoute('leaders', selectedArtist?.id, 'profile');
      navigate(artistProfileRoute, { replace: true });
      return;
    }

    // there's an artist id url param but no selected artist id
    if (artistIdUrlParam && !selectedArtist?.id) {
      setSelectedArtist({ ...initialArtistData, id: artistIdUrlParam });
    }

    // URL changed to a different artist than context — switch if user has access
    const urlParamChanged = artistIdUrlParam !== prevUrlParamRef.current;
    prevUrlParamRef.current = artistIdUrlParam;

    if (urlParamChanged && artistIdUrlParam && selectedArtist?.id && artistIdUrlParam !== selectedArtist.id) {
      // Delegate access-checking to the server: a stub makes StudioContext
      // fetch the artist, and a 403/404 lands in its explicit denied path.
      // An artists.find() gate alone breaks for admins whose target is beyond
      // the first paginated page and silently does nothing on no-access.
      const targetArtist = artists.find((a) => a.id === artistIdUrlParam);
      setSelectedArtist(targetArtist || { ...initialArtistData, id: artistIdUrlParam });
    }
  }, [isUserRegistered, artistIdUrlParam, selectedArtist?.id, location.pathname, artists]);

  const asRoute = (...parts) => {
    const path = parts.filter(Boolean).join('/');
    return path.startsWith('/') ? path : `/${path}`;
  };

  // Legacy branch is dead until C11 deletes the old pages.
  const aiOrLegacy = (aiParts, legacyParts) => asRoute(...(isAiSettingsEnabled ? aiParts : legacyParts));
  const aiSettingsWriting = () => aiOrLegacy(['settings', 'ai', 'writing'], ['settings', 'workflow-ai']);
  const aiSettingsChat = () => aiOrLegacy(['settings', 'ai', 'chat'], ['settings', 'chatbot']);
  const aiSettingsVoices = () => aiOrLegacy(['settings', 'ai', 'voices'], ['settings', 'voices']);
  const aiSettingsLanguages = () => aiOrLegacy(['settings', 'ai', 'languages'], ['settings', 'translations']);
  const adminChatPrompts = () => aiOrLegacy(['admin', 'chat-prompts'], ['settings', 'chatbot-prompts']);

  return {
    artistId,

    // ACCOUNT ROUTES (current user) -----------------------

    accountProfile: () => asRoute('account', 'profile'),

    // ARTIST ROUTES ---------------------------------------

    // artist dashboard
    artistDashboard: () => asRoute('leaders', artistId, 'dashboard'),

    // artist calendar
    artistCalendar: () => asRoute('leaders', artistId, 'calendar'),

    // artist pray tv (now under Library)
    artistPrayTv: () => asRoute('leaders', artistId, 'library', 'pray-tv'),

    // artist pray radio (separate tab under Library)
    artistPrayRadio: () => asRoute('leaders', artistId, 'library', 'pray-radio'),

    // artist cover arts
    artistCoverArts: () => asRoute('leaders', artistId, 'cover-arts'),

    // artist profile
    artistProfile: (id = '') => asRoute('leaders', id || artistId, 'profile'),
    // artist-less profile route: the create-leader form (isNewArtistProfile branch in ProfilePage)
    newLeaderProfile: () => asRoute('leaders', 'profile'),

    // artist content (legacy routes - redirect to library/episodes)
    artistContent: () => asRoute('leaders', artistId, 'library', 'episodes'),
    artistContentAudio: () => asRoute('leaders', artistId, 'library', 'episodes', 'audio'),
    artistContentVideo: () => asRoute('leaders', artistId, 'library', 'episodes', 'video'),

    // episodes under library (new canonical location)
    artistLibraryEpisodes: () => asRoute('leaders', artistId, 'library', 'episodes'),
    artistLibraryEpisodesAudio: () => asRoute('leaders', artistId, 'library', 'episodes', 'audio'),
    artistLibraryEpisodesVideo: () => asRoute('leaders', artistId, 'library', 'episodes', 'video'),
    artistContentDetails: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'details'),
    artistContentDetailsForArtist: (targetArtistId, contentId) =>
      asRoute('leaders', targetArtistId, 'episodes', contentId, 'details'),
    artistContentSummary: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'summary'),
    artistContentTranscript: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'transcript'),
    artistContentTranscriptVoices: (contentId) =>
      asRoute('leaders', artistId, 'episodes', contentId, 'transcript', 'voices'),
    artistContentAnnouncement: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'announcement'),
    artistContentEmail: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'email'),

    artistContentArticle: (id) => asRoute('leaders', artistId, 'episodes', id, 'article'),
    artistContentSocialClips: (contentId) => asRoute('leaders', artistId, 'episodes', contentId, 'social-clips'),
    artistContentSocialClipsPublish: (contentId, socialClipId) =>
      asRoute('leaders', artistId, 'episodes', contentId, 'social-clips', socialClipId, 'publish'),

    artistContentStudyGuide: (id) => asRoute('leaders', artistId, 'episodes', id, 'study-guide'),

    // artist revenue (formerly donations)
    artistRevenue: () => asRoute('leaders', artistId, 'revenue'),
    artistRevenueOverview: (id = '') => asRoute('leaders', id || artistId, 'revenue', 'overview'),
    artistRevenueRetention: () => asRoute('leaders', artistId, 'revenue', 'retention'),
    artistRevenueSubscribers: () => asRoute('leaders', artistId, 'revenue', 'subscribers'),
    artistRevenueTransactions: () => asRoute('leaders', artistId, 'revenue', 'transactions'),
    artistRevenueOrganizations: () => asRoute('leaders', artistId, 'revenue', 'organizations'),
    artistRevenueFunds: () => asRoute('leaders', artistId, 'revenue', 'funds'),
    artistRevenueFundsCreate: () => asRoute('leaders', artistId, 'revenue', 'funds', 'create'),
    artistRevenueFundsEdit: (id) => asRoute('leaders', artistId, 'revenue', 'funds', id, 'edit'),
    artistRevenuePayouts: () => asRoute('leaders', artistId, 'revenue', 'payouts'),
    artistRevenuePayout: (id) => asRoute('leaders', artistId, 'revenue', 'payouts', id),
    artistRevenuePayoutDetails: (id) => asRoute('leaders', artistId, 'revenue', 'payouts', id),
    artistRevenueContracts: () => asRoute('leaders', artistId, 'revenue', 'contracts'),
    artistRevenueContract: (id) => asRoute('leaders', artistId, 'revenue', 'contracts', id),
    artistRevenueStatements: () => asRoute('leaders', artistId, 'revenue', 'statements'),
    // SuperFunnel self-serve (owner) — flag-gated sub-tab under Revenue
    artistRevenueSuperFunnel: () => asRoute('leaders', artistId, 'revenue', 'superfunnel'),
    artistRevenueSuperFunnelManage: () => asRoute('leaders', artistId, 'revenue', 'superfunnel', 'manage'),

    // Legacy routes (backward compatibility) - now redirect to revenue
    artistDonationsSummary: () => asRoute('leaders', artistId, 'revenue', 'subscribers'),
    artistDonationsAnalytics: () => asRoute('leaders', artistId, 'revenue', 'overview'),
    artistDonationsFunds: () => asRoute('leaders', artistId, 'revenue', 'funds'),
    artistDonationsBankPayouts: () => asRoute('leaders', artistId, 'revenue', 'payouts'),
    artistDonationsFundsCreate: () => asRoute('leaders', artistId, 'revenue', 'funds', 'create'),
    artistDonationsFundsEdit: (id) => asRoute('leaders', artistId, 'revenue', 'funds', id, 'edit'),
    artistDonationsStatements: () => asRoute('leaders', artistId, 'revenue', 'statements'),
    artistDonationsBankPayout: (id) => asRoute('leaders', artistId, 'revenue', 'payouts', id),

    // Library routes
    artistLibrary: () => asRoute('leaders', artistId, 'library'),
    //  books
    artistLibraryBooks: () => asRoute('leaders', artistId, 'library', 'books'),
    artistLibraryBookDetails: (id) => asRoute('leaders', artistId, 'library', 'books', id, 'details'),
    artistLibraryBookTranscript: (id) => asRoute('leaders', artistId, 'library', 'books', id, 'transcript'),
    artistLibraryBookTranscriptEdit: (id) => asRoute('leaders', artistId, 'library', 'books', id, 'transcript', 'edit'),
    artistLibraryBookTranscriptEditVoices: (id) =>
      asRoute('leaders', artistId, 'library', 'books', id, 'transcript', 'edit', 'voices'),
    //  articles
    artistLibraryArticles: () => asRoute('leaders', artistId, 'library', 'articles'),
    artistLibraryArticleEditor: (id) => asRoute('leaders', artistId, 'library', 'articles', id),

    //  series
    artistLibraryPlaylists: () => asRoute('leaders', artistId, 'library', 'series'),
    artistLibraryContentPlaylists: () => asRoute('leaders', artistId, 'library', 'series', 'content'),
    artistLibraryDailiesPlaylists: () => asRoute('leaders', artistId, 'library', 'series', 'dailies'),
    artistLibraryPlaylistCreate: () => asRoute('leaders', artistId, 'library', 'series', 'create'),
    artistLibraryPlaylistDetails: (id) => asRoute('leaders', artistId, 'library', 'series', id),
    artistLibraryPlaylistDetailsForm: (id) => asRoute('leaders', artistId, 'library', 'series', id, 'details'),
    artistLibraryPlaylistDetailsForArtist: (targetArtistId, id) =>
      asRoute('leaders', targetArtistId, 'library', 'series', id),
    artistLibraryPlaylistDetailsFormForArtist: (targetArtistId, id) =>
      asRoute('leaders', targetArtistId, 'library', 'series', id, 'details'),
    artistLibraryPlaylistDistribution: (id) => asRoute('leaders', artistId, 'library', 'series', id, 'distribution'),
    //  daily series
    artistLibraryDailies: () => asRoute('leaders', artistId, 'library', 'dailies'),
    artistLibraryDailyPlaylistDetails: (id) => asRoute('leaders', artistId, 'library', 'daily-series', id),
    artistLibraryDailySeriesDetailsForm: (id) => asRoute('leaders', artistId, 'library', 'daily-series', id, 'details'),
    artistLibraryDailySeriesDetailsCoverArtForm: (id) =>
      asRoute('leaders', artistId, 'library', 'daily-series', id, 'cover-art'),
    artistLibraryDailySeriesDetailsPopUpsForm: (id) =>
      asRoute('leaders', artistId, 'library', 'daily-series', id, 'popups'),
    //  rss feed
    artistLibraryRssFeedCreate: () => asRoute('leaders', artistId, 'library', 'rss-feed', 'create'),
    artistLibraryRssFeedDetails: (id) => asRoute('leaders', artistId, 'library', 'rss-feed', id),
    artistLibraryRssFeedDetailsForm: (id) => asRoute('leaders', artistId, 'library', 'rss-feed', id, 'details'),
    artistLibraryRssFeedDistribution: (id) => asRoute('leaders', artistId, 'library', 'rss-feed', id, 'distribution'),
    artistLibraryRssFeedEmailTemplate: (id) =>
      asRoute('leaders', artistId, 'library', 'rss-feed', id, 'email-template'),
    artistLibraryRssFeedEmailSegment: (id) => asRoute('leaders', artistId, 'library', 'rss-feed', id, 'email-segment'),

    //  youtube playlist
    artistLibraryYouTubePlaylistDetails: (id) => asRoute('leaders', artistId, 'library', 'youtube-playlist', id),

    // artist banner
    artistBanner: () => asRoute('leaders', artistId, 'banner'),

    // Legacy standalone announcement routes (System A) retired 2026-07 — use the
    // discipleship* announcement helpers below instead.

    // artist email campaigns
    artistEmailCampaignEdit: (id) => asRoute('leaders', artistId, 'emails', id, 'edit'),
    artistEmailCampaignAnalyticsOverview: (id) => asRoute('leaders', artistId, 'emails', id, 'analytics'),

    // artist emails
    artistEmails: () => asRoute('leaders', artistId, 'emails'),
    artistEmailsCreate: () => asRoute('leaders', artistId, 'emails', 'create'),
    artistEmailsEdit: (id) => asRoute('leaders', artistId, 'emails', id, 'edit'),
    artistEmailsAnalytics: (id) => asRoute('leaders', artistId, 'emails', id, 'analytics'),
    artistEmailsOverview: (id) => asRoute('leaders', artistId, 'emails', id, 'overview'),

    // People Tab (consolidated: Followers, Lists, Segments, Team)
    artistPeople: () => asRoute('leaders', artistId, 'people'),
    artistPeopleFollowers: () => asRoute('leaders', artistId, 'people', 'followers'),
    artistPeopleLists: () => asRoute('leaders', artistId, 'people', 'lists'),
    artistPeopleListDetails: (listId) => asRoute('leaders', artistId, 'people', 'lists', listId),
    artistPeopleListUpload: () => asRoute('leaders', artistId, 'people', 'lists', 'upload'),
    artistPeopleListAddContacts: (listId) => asRoute('leaders', artistId, 'people', 'lists', listId, 'add-contacts'),
    artistPeopleSegments: () => asRoute('leaders', artistId, 'people', 'segments'),
    artistPeopleSegmentsCreate: () => asRoute('leaders', artistId, 'people', 'segments', 'create'),
    artistPeopleSegmentDetails: (segmentId) => asRoute('leaders', artistId, 'people', 'segments', segmentId),
    artistPeoplePeriodicSegments: () => asRoute('leaders', artistId, 'people', 'periodic-segments'),
    artistPeopleTeam: () => asRoute('leaders', artistId, 'people', 'team'),

    // Legacy routes (for backward compatibility - these redirect to People Tab)
    // artist followers
    artistFollowers: () => asRoute('leaders', artistId, 'followers'),

    // artist periodic segments
    artistPeriodicSegments: () => asRoute('leaders', artistId, 'periodic-segments'),

    // artist lead segments
    artistSegments: () => asRoute('leaders', artistId, 'segments'),
    artistSegmentsCreate: () => asRoute('leaders', artistId, 'segments', 'create'),
    artistSegmentDetails: (segmentId) => asRoute('leaders', artistId, 'segments', segmentId),

    // artist team
    artistTeam: () => asRoute('leaders', artistId, 'team'),

    // artist offers (nested under Library)
    artistOffers: () => asRoute('leaders', artistId, 'library', 'offers'),
    artistOffersEdit: (id) => asRoute('leaders', artistId, 'library', 'offers', id, 'edit'),
    artistOffersAnalytics: (id) => asRoute('leaders', artistId, 'library', 'offers', id, 'analytics'),

    // artist lists
    artistLists: () => asRoute('leaders', artistId, 'lists'),
    artistContactsListDetails: (id) => asRoute('leaders', artistId, 'lists', id, 'details'),
    artistContactsListUpload: () => asRoute('leaders', artistId, 'lists', 'upload'),
    artistContactsListAddContacts: (id) => asRoute('leaders', artistId, 'lists', id, 'add-contacts'),

    // artist ministry match (legacy routes - redirect to marketing)
    ministryMatchCampaigns: () => asRoute('marketing', 'leads'),
    ministryMatchCampaignDetails: (id) => asRoute('marketing', 'leads', id),
    ministryMatchOrganizations: () => asRoute('marketing', 'integrations'),
    ministryMatchOrganizationDetails: (id) => asRoute('marketing', 'integrations', id),

    // help / FAQ routes
    help: () => asRoute('help'),
    helpCategory: (categoryId) => asRoute('help', categoryId),
    /** Deep link straight to one answer, used by info buttons across the app. */
    helpAnswer: (categoryId, entryId) => `${asRoute('help', categoryId)}?q=${entryId}`,

    // global ticket workspace (not artist-scoped)
    tickets: () => asRoute('tickets'),

    // marketing routes
    marketing: () => asRoute('marketing'),
    marketingCampaigns: () => asRoute('marketing', 'leads'),
    marketingCampaignDetails: (id) => asRoute('marketing', 'leads', id),
    marketingOrganizations: () => asRoute('marketing', 'integrations'),
    marketingOrganizationDetails: (id) => asRoute('marketing', 'integrations', id),
    marketingIntegrations: () => asRoute('marketing', 'integrations'),
    marketingIntegrationDetails: (id) => asRoute('marketing', 'integrations', id),

    // email health routes
    marketingEmailHealth: () => asRoute('marketing', 'email-health'),
    marketingEmailHealthDetail: (orgId) => asRoute('marketing', 'email-health', orgId),

    // superfunnel routes
    marketingSuperfunnel: () => asRoute('marketing', 'superfunnel'),
    marketingSuperfunnelCreate: () => asRoute('marketing', 'superfunnel', 'create'),
    marketingSuperfunnelDetails: (id) => asRoute('marketing', 'superfunnel', id),
    marketingSuperfunnelEdit: (id) => asRoute('marketing', 'superfunnel', id, 'edit'),

    // discipleship routes (consolidated communications tab)
    discipleship: () => asRoute('leaders', artistId, 'discipleship'),
    discipleshipConversations: () => asRoute('leaders', artistId, 'discipleship', 'conversations'),
    discipleshipEmailAutomations: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'automations'),
    discipleshipEmailAutomationDetails: (automationId) =>
      asRoute('leaders', artistId, 'discipleship', 'emails', 'automations', automationId),
    discipleshipEmailAutomationDetailsForArtist: (targetArtistId, automationId) =>
      asRoute('leaders', targetArtistId, 'discipleship', 'emails', 'automations', automationId),
    discipleshipEmails: () => asRoute('leaders', artistId, 'discipleship', 'emails'),
    discipleshipEmailsCreate: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'campaigns', 'create'),
    discipleshipEmailsEdit: (id) => asRoute('leaders', artistId, 'discipleship', 'emails', 'campaigns', id, 'edit'),
    discipleshipEmailsOverview: (id) =>
      asRoute('leaders', artistId, 'discipleship', 'emails', 'campaigns', id, 'overview'),
    discipleshipEmailsAnalytics: (id) =>
      asRoute('leaders', artistId, 'discipleship', 'emails', 'campaigns', id, 'analytics'),
    discipleshipPush: () => asRoute('leaders', artistId, 'discipleship', 'push'),
    discipleshipPushCreate: () => asRoute('leaders', artistId, 'discipleship', 'push', 'campaigns', 'create'),
    discipleshipPushEdit: (id) => asRoute('leaders', artistId, 'discipleship', 'push', 'campaigns', id, 'edit'),
    discipleshipCatalogs: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'catalogs'),
    discipleshipCatalogNew: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'catalogs', 'new'),
    discipleshipCatalog: (catalogId) => asRoute('leaders', artistId, 'discipleship', 'emails', 'catalogs', catalogId),
    discipleshipWarmups: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'warmup'),
    discipleshipWarmupNew: () => asRoute('leaders', artistId, 'discipleship', 'emails', 'warmup', 'new'),
    discipleshipWarmup: (warmupId) => asRoute('leaders', artistId, 'discipleship', 'emails', 'warmup', warmupId),
    discipleshipAnnouncements: () => asRoute('leaders', artistId, 'discipleship', 'announcements'),
    discipleshipAnnouncementCreate: () => asRoute('leaders', artistId, 'discipleship', 'announcements', 'create'),
    discipleshipAnnouncementEdit: (id) => asRoute('leaders', artistId, 'discipleship', 'announcements', id, 'edit'),
    discipleshipAnnouncementOverview: (id) =>
      asRoute('leaders', artistId, 'discipleship', 'announcements', id, 'overview'),
    discipleshipAnnouncementAnalytics: (id) =>
      asRoute('leaders', artistId, 'discipleship', 'announcements', id, 'analytics'),
    discipleshipChats: () => asRoute('leaders', artistId, 'discipleship', 'chats'),
    discipleshipChatsUserId: (userId) => asRoute('leaders', artistId, 'discipleship', 'chats', userId),
    discipleshipTestimonials: () => asRoute('leaders', artistId, 'discipleship', 'testimonials'),
    // profile segments are platform-wide (Pray studio admins on the Pray artist); the leader in the path is context only
    discipleshipSegments: () => asRoute('leaders', artistId, 'discipleship', 'segments'),
    discipleshipSegmentNew: () => asRoute('leaders', artistId, 'discipleship', 'segments', 'new'),
    discipleshipSegmentRefresh: () => asRoute('leaders', artistId, 'discipleship', 'segments', 'refresh-health'),
    discipleshipSegment: (segmentId) => asRoute('leaders', artistId, 'discipleship', 'segments', segmentId),

    // GENERAL ROUTES ---------------------------------------

    // CRM analytics (admin)
    adminCrmAnalytics: () => asRoute('admin', 'crm-analytics'),
    adminUniversalLinks: () => asRoute('admin', 'universal-links'),
    adminSeedEmails: () => asRoute('admin', 'seed-emails'),
    adminAccountStatus: () => asRoute('admin', 'account-status'),

    // Name capture (admin)
    adminNameCapture: () => asRoute('admin', 'name-capture'),

    // index
    index: () => '/',

    // preview
    preview: () => '/preview/*',
    previewDonation: () => '/preview/donation',

    // studio
    studio: () => '/*',

    // healthcheck
    healthCheck: () => '/web-healthcheck',

    // dailies
    dailies: () => '/dailies',
    dailySeriesIdDailyItemsId: (dailySeriesId, dailyItemId) =>
      asRoute('dailies', 'daily-series', dailySeriesId, 'daily-items', dailyItemId),

    // tags (legacy routes - redirect to library)
    tags: () => `/tags`,
    tagDetails: (id) => `/tags/${id}`,

    // tags under library (new location)
    artistLibraryTags: () => asRoute('leaders', artistId, 'library', 'tags'),
    artistLibraryTagDetails: (id) => asRoute('leaders', artistId, 'library', 'tags', id),

    // daily builder under library (new location)
    artistLibraryDailyBuilder: () => asRoute('leaders', artistId, 'library', 'daily-builder'),
    artistLibraryDailyBuilderCreate: () => asRoute('leaders', artistId, 'library', 'daily-builder', 'create'),
    artistLibraryDailyBuilderCreateRss: () => asRoute('leaders', artistId, 'library', 'daily-builder', 'create', 'rss'),
    artistLibraryDailyBuilderItem: (dailySeriesId, dailyItemId) =>
      asRoute(
        'leaders',
        artistId,
        'library',
        'daily-builder',
        'daily-series',
        dailySeriesId,
        'daily-items',
        dailyItemId
      ),

    // analytics
    analytics: () => asRoute('leaders', artistId, 'analytics'),
    analyticsListen: () => asRoute('leaders', artistId, 'analytics', 'listen'),
    analyticsWatch: () => asRoute('leaders', artistId, 'analytics', 'watch'),
    analyticsAudience: () => asRoute('leaders', artistId, 'analytics', 'audience'),
    analyticsTestimonials: () => asRoute('leaders', artistId, 'analytics', 'testimonials'),
    analyticsPeriodicTable: () => asRoute('leaders', artistId, 'analytics', 'periodic-table'),
    analyticsSegment: (id) => asRoute('leaders', artistId, 'analytics', 'periodic-table', 'segments', id),

    // impact reports (now under analytics)
    impact: () => asRoute('leaders', artistId, 'analytics', 'impact'),
    impactCreate: () => asRoute('leaders', artistId, 'analytics', 'impact', 'create'),

    // settings
    settingsAi: () => asRoute('settings', 'ai'),
    settingsAiWriting: aiSettingsWriting,
    settingsAiTranscription: () => asRoute('settings', 'ai', 'transcription'),
    settingsAiChat: aiSettingsChat,
    settingsAiVoices: aiSettingsVoices,
    settingsAiLanguages: aiSettingsLanguages,
    adminChatPrompts,
    // Legacy names return the same paths as the new builders, so no call site breaks.
    settingsWorkflowAI: aiSettingsWriting,
    settingsChatbot: aiSettingsChat,
    settingsVoices: aiSettingsVoices,
    settingsTranslations: aiSettingsLanguages,
    settingsChatbotPrompts: adminChatPrompts,
    settingsAccount: () => asRoute('settings', 'account'),
    settingsSuperFunnelAutomations: () => asRoute('settings', 'superfunnel-automations'),
    settingsSuperFunnelAutomationsNew: () => asRoute('settings', 'superfunnel-automations', 'new'),
    settingsSuperFunnelWeeklyReport: () => asRoute('settings', 'superfunnel-weekly-report'),
    settingsTranslationsStats: () => asRoute('settings', 'video-translations-stats'),
    settingsSlotsLimits: () => asRoute('settings', 'slots-limits'),
    settingsCreditsAdmin: () => asRoute('settings', 'admin-credits'),
    settingsCredits: () => asRoute('settings', 'credits'),
    settingsAvatars: () => asRoute('settings', 'avatars'),
    settingsConversations: () => asRoute('settings', 'conversations'),
    settingsRevenue: () => asRoute('settings', 'revenue'),
    settingsElevenLabsConfig: () => asRoute('settings', 'elevenlabs-config'),
    settingsMembers: () => asRoute('settings', 'members'),
    settingsMemberDetails: (memberId, tab) => asRoute('settings', 'members', memberId, tab),
    settingsMembersUnsubscribes: () => asRoute('settings', 'members', 'unsubscribes'),
    settingsOrganizations: () => asRoute('settings', 'organizations'),
    settingsOrganizationDetail: (organizationId) => asRoute('settings', 'organizations', organizationId),
    settingsProducts: () => asRoute('settings', 'products'),
    settingsSeries: () => asRoute('settings', 'series'),
    settingsSubscriptions: () => asRoute('settings', 'subscriptions'),
    settingsPurchases: () => asRoute('settings', 'purchases'),
    settingsGivingAccounts: () => asRoute('settings', 'giving-accounts'),
    settingsLeaders: () => asRoute('settings', 'leaders'),
    settingsOrganizationsMerge: () => asRoute('settings', 'organizations', 'merge'),
    settingsContentCategories: () => asRoute('settings', 'content-categories'),
    settingsAbnTesting: () => asRoute('settings', 'abn-testing'),
    settingsAbnTestingInstance: (instanceName) => asRoute('settings', 'abn-testing', encodeURIComponent(instanceName)),
    settingsAbnTestingInstanceCreate: (instanceName) =>
      asRoute('settings', 'abn-testing', encodeURIComponent(instanceName), 'create'),
    settingsSocialAccounts: () => asRoute('settings', 'social-accounts'),
    settingsBrandKit: () => asRoute('settings', 'brand-kit'),

    // impact reports (now under analytics)
    artistImpact: () => asRoute('leaders', artistId, 'analytics', 'impact'),
    artistImpactCreate: () => asRoute('leaders', artistId, 'analytics', 'impact', 'create'),
    artistImpactClarify: (reportId) =>
      asRoute('leaders', artistId, 'analytics', 'impact', 'create', reportId, 'clarify'),
    artistImpactGenerate: (reportId) => asRoute('leaders', artistId, 'analytics', 'impact', 'generate', reportId),
    artistImpactReport: (reportId) => asRoute('leaders', artistId, 'analytics', 'impact', reportId),

    // email configuration
    emailConfig: () => asRoute('email-config'),
    emailConfigDomains: () => asRoute('email-config', 'domains'),
    emailConfigSenders: () => asRoute('email-config', 'senders'),
    emailConfigUnsubscribeGroups: () => asRoute('email-config', 'unsubscribe-groups'),
    emailConfigIpPools: () => asRoute('email-config', 'ip-pools'),
    emailConfigCustomDomainSetup: () => asRoute('email-config', 'custom-domain-setup'),

    // external redirects
    revenueExternalPage: () => asRoute('revenue', 'external-page'),
    creditsExternalPage: () => asRoute('credits', 'external-page'),
    oauthYoutubeCallback: () => asRoute('oauth', 'youtube', 'callback'),

    // disputes
    disputes: () => asRoute('settings', 'disputes'),

    // feature flags
    settingsFeatureFlags: () => asRoute('settings', 'feature-flags'),
    settingsFeatureFlagsCreate: () => asRoute('settings', 'feature-flags', 'create'),
    settingsFeatureFlagDetail: (flagId) => asRoute('settings', 'feature-flags', flagId),
    settingsFeatureFlagEdit: (flagId) => asRoute('settings', 'feature-flags', flagId, 'edit'),

    // system check
    systemCheck: () => asRoute('system-check'),
    systemCheckTranslations: () => asRoute('system-check', 'translations'),
    systemCheckWorkflow: () => asRoute('system-check', 'workflow'),
    systemCheckSocialClips: () => asRoute('system-check', 'social-clips'),
    systemCheckDailies: () => asRoute('system-check', 'dailies'),

    // experiments
    experiments: () => asRoute('experiments'),
    experimentsCreate: () => asRoute('experiments', 'create'),

    experimentsInstance: (instanceName) => asRoute('experiments', 'instance', instanceName),
    experimentsInstanceCreate: (instanceName) => asRoute('experiments', 'instance', instanceName, 'create'),
    experimentsDetails: (id) => asRoute('experiments', id),

    // pricing
    pricing: () => asRoute('pricing'),

    // PLG Landing Pages
    products: () => '/products/*',
    socialClips: () => asRoute('products', 'social-clips'),
    translations: () => asRoute('products', 'ai-translations'),
    ministryInsights: () => asRoute('products', 'ministry-insights'),
    crm: () => asRoute('products', 'crm'),
    workflowAi: () => asRoute('products', 'workflow-ai'),
    audioBooks: () => asRoute('products', 'ai-audiobooks'),
    cms: () => asRoute('products', 'cms'),

    // PLG Onboarding
    getStarted: () => asRoute('get-started'),

    // Public: the SuperFunnel weekly report email's unsubscribe link (STUDIO-176). server_pray builds
    // this URL, so it must not move without that sender changing too.
    weeklyReportUnsubscribe: () => asRoute('weekly-report', 'unsubscribe'),

    // Public: the same email's "Open your Revenue Overview" button, which records the click and then opens
    // the ministry's overview (STUDIO-176). server_pray builds this URL (SuperFunnelWeeklyReportSendService),
    // so it must not move without that sender changing too.
    weeklyReportOpen: () => asRoute('weekly-report', 'open'),

    // login
    login: () => asRoute('login'),

    // initial route
    initial: () => {
      let leaderId = artistId || artists[0]?.id;

      if (!leaderId) {
        return asRoute('leaders', 'profile');
      }

      // Only consult the stored last leader when there is no live selection;
      // a URL-seeded or user-picked leader must not be overridden by storage.
      if (!artistId) {
        const storedArtistId = storage.getFromStorage(LOCAL_STORAGE.SELECTED_ARTIST_ID);
        leaderId = artists.find((artist) => artist.id === storedArtistId)?.id || leaderId;
      }

      return asRoute('leaders', leaderId, 'dashboard');
    },
  };
}
